import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAccountApi } from '@/services/AccountApi';
import { type ILogger } from '@/services/Logger';
import { type AdminSession } from '@/domain/admin';

/**
 * The account self-service ViewModel: a signed-in user browses their OWN active sessions, logs out a single
 * one, logs out everywhere (the authoritative Sub-watermark kill), or CHANGES their password (re-auth with the
 * current password; the change revokes every session so a re-login is required). Verified against
 * `FakeAccountApi`.
 */
export class AccountStore {
  private _sessions: AdminSession[] = [];
  private _loading = false;
  private _busy = false;
  private _error: string | null = null;
  private _notice: string | null = null;
  private _passwordChanged = false;

  constructor(
    private api: IAccountApi,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get sessions(): readonly AdminSession[] {
    return this._sessions;
  }
  get loading(): boolean {
    return this._loading;
  }
  get busy(): boolean {
    return this._busy;
  }
  get error(): string | null {
    return this._error;
  }
  get notice(): string | null {
    return this._notice;
  }
  /** True after a successful password change: the view prompts a re-login (the old token is now revoked). */
  get passwordChanged(): boolean {
    return this._passwordChanged;
  }

  /** Change the caller's own password. Client-side: both fields required, the new one >= 8 chars and different
   * from the current. The re-auth + revocation is the server's job; on success `passwordChanged` flips true. */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!currentPassword || !newPassword) {
      this._error = 'both the current and new password are required';
      return;
    }
    if (newPassword.length < 8) {
      this._error = 'the new password must be at least 8 characters';
      return;
    }
    if (newPassword === currentPassword) {
      this._error = 'the new password must be different from the current one';
      return;
    }
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      await this.api.changePassword(currentPassword, newPassword);
      runInAction(() => {
        this._passwordChanged = true;
        this._notice = 'Password changed. Please sign in again.';
        this._sessions = [];
      });
    } catch (e) {
      this.fail('change password', e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  async load(): Promise<void> {
    this._loading = true;
    this._error = null;
    try {
      const sessions = await this.api.listSessions();
      runInAction(() => (this._sessions = sessions));
    } catch (e) {
      this.fail('load sessions', e);
    } finally {
      runInAction(() => (this._loading = false));
    }
  }

  async logoutOne(sessionId: string): Promise<void> {
    await this.run('logged out the session', async () => {
      await this.api.logoutOne(sessionId);
      const sessions = await this.api.listSessions();
      runInAction(() => (this._sessions = sessions));
    });
  }

  async logoutAll(): Promise<void> {
    await this.run('logged out everywhere', async () => {
      await this.api.logoutAll();
      runInAction(() => (this._sessions = []));
    });
  }

  private async run(label: string, fn: () => Promise<void>): Promise<void> {
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      await fn();
      runInAction(() => (this._notice = label));
    } catch (e) {
      this.fail(label, e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  private fail(what: string, e: unknown): void {
    this.logger.error(`${what} failed`, e);
    runInAction(() => (this._error = `${what} failed: ${e instanceof Error ? e.message : String(e)}`));
  }
}

export const TOKEN_AccountStore = token<AccountStore>('AccountStore');
